'use client';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import authService from '@/services/supabase/auth.service';
import { ReferralService } from '@/services/supabase/referral.service';
import { supabase } from '@/services/supabase/supabase';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import { Camera, Check, ChevronLeft, ChevronRight, FileText, MapPin, Phone, Upload, User, X } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from '@/lib/utils';

// ─── Schema ─────────────────────────────────────────────────────────────────

const signupSchema = z
  .object({
    firstName: z.string().min(1, { message: 'First name is required.' }),
    lastName: z.string().min(1, { message: 'Last name is required.' }),
    hometown: z.string().min(1, { message: 'Hometown is required.' }),
    country: z.string().min(1, { message: 'Country of origin is required.' }),
    email: z.string().email({ message: 'Please enter a valid email.' }),
    phone: z.string().min(7, { message: 'Please enter a valid phone number.' }),
    password: z.string().min(8, { message: 'Password must be at least 8 characters.' }),
    confirmPassword: z.string().min(1, { message: 'Please confirm your password.' }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type SignupValues = z.infer<typeof signupSchema>;

// ─── Config ─────────────────────────────────────────────────────────────────

import { ScrollReveal } from '@/components/animations/ScrollReveal';

const STEPS = [
  { id: 1, label: 'Personal', Icon: User },
  { id: 2, label: 'Contact', Icon: Phone },
  { id: 3, label: 'Identity', Icon: FileText },
];

const STEP_FIELDS: Record<number, (keyof SignupValues)[]> = {
  1: ['firstName', 'lastName', 'hometown', 'country'],
  2: ['email', 'phone', 'password', 'confirmPassword'],
  3: [],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Resizes and compresses an image file using browser Canvas API.
 * Reduces raw multi-MB photos to a manageable size (max 1600px width/height, 0.7 quality).
 */
async function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onerror = reject;
    reader.onload = (event) => {
      const img = new window.Image();
      img.src = event.target?.result as string;
      img.onerror = reject;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_DIM = 1600;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_DIM) {
            height *= MAX_DIM / width;
            width = MAX_DIM;
          }
        } else {
          if (height > MAX_DIM) {
            width *= MAX_DIM / height;
            height = MAX_DIM;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas compression failed'));
        }, 'image/jpeg', 0.7);
      };
    };
  });
}

async function uploadToStorage(file: File, bucket: string, user_id: string, filename: string): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'bin';
  const path = `${user_id}/${filename}.${ext}`;

  // Compress if it's an image
  let fileToUpload: Blob | File = file;
  if (file.type.startsWith('image/')) {
    try {
      fileToUpload = await compressImage(file);
    } catch (e) {
      console.warn('Image compression failed, uploading original:', e);
    }
  }

  const { error } = await supabase.storage.from(bucket).upload(path, fileToUpload, { upsert: true });
  if (error) throw error;
  return path;
}

// ─── Form Component ──────────────────────────────────────────────────────────

function SignupForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  const [idFile, setIdFile] = useState<File | null>(null);
  const [idPreview, setIdPreview] = useState<string | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setReferralCode(ref);
  }, [searchParams]);

  useEffect(() => {
    return () => { cameraStream?.getTracks().forEach((t) => t.stop()); };
  }, [cameraStream]);

  const form = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '', lastName: '',
      hometown: '', country: '',
      email: '', phone: '',
      password: '', confirmPassword: '',
    },
  });

  const goNext = async () => {
    const valid = await form.trigger(STEP_FIELDS[step]);
    if (valid) setStep((s) => s + 1);
  };

  const goPrev = () => setStep((s) => s - 1);

  const startCamera = async () => {
    // Check if mediaDevices is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast({
        variant: 'destructive',
        title: 'Camera Not Supported',
        description: 'Your browser doesn\'t support camera access. Please try a modern browser.'
      });
      return;
    }

    try {
      // First attempt: Optimal settings for "user" camera
      const constraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e: any) {
        console.warn('Initial camera constraints failed, trying fallback 1 (any video)...', e);
        try {
          // Fallback 1: Just any video device
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        } catch (e2: any) {
          console.warn('Fallback 1 failed, trying fallback 2 (enumerate devices)...', e2);
          // Fallback 2: Get all devices and try the first videoinput
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter(d => d.kind === 'videoinput');

          if (videoDevices.length > 0) {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: { exact: videoDevices[0].deviceId } }
            });
          } else {
            throw new Error('No video input devices found.');
          }
        }
      }

      setCameraStream(stream);
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Ensure video plays after setting srcObject
        videoRef.current.play().catch(err => {
          console.error("Video play error:", err);
          setCameraActive(false);
          setCameraStream(null);
        });
      }
    } catch (err: any) {
      console.error('Camera access completely failed:', err);
      toast({
        variant: 'destructive',
        title: 'Camera Error',
        description: `Could not access camera: ${err.message || 'Please ensure permissions are granted.'}`
      });
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      // Selfie also benefits from slightly lower quality to speed up display later
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      setSelfieFile(file);
      setSelfiePreview(canvas.toDataURL('image/jpeg', 0.7));
      cameraStream?.getTracks().forEach((t) => t.stop());
      setCameraActive(false);
      setCameraStream(null);
    }, 'image/jpeg', 0.7);
  };

  const stopCamera = () => {
    cameraStream?.getTracks().forEach((t) => t.stop());
    setCameraActive(false);
    setCameraStream(null);
  };

  const onSubmit = async (values: SignupValues) => {
    // Prevent submission if not on the final step
    if (step < STEPS.length) {
      goNext();
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await authService.register(
        values.email,
        values.password,
        values.firstName,
        values.lastName,
        values.hometown,
        values.country,
        values.phone,
        'user',
      );

      if (error) {
        if ((error as any)?.message === 'User already registered') {
          toast({
            variant: 'destructive',
            title: 'Account Exists',
            description: 'This email is already registered. Please try logging in instead.'
          });
          // router.push('/login'); // Optional: could auto-redirect
          return;
        }
        toast({ variant: 'destructive', title: 'Registration Error', description: (error as any)?.message || 'An error occurred during account creation.' });
        return;
      }

      const user_id = data?.user?.id;

      if (user_id) {
        let idDocumentUrl: string | undefined;
        let selfieUrl: string | undefined;

        if (idFile) {
          try { idDocumentUrl = await uploadToStorage(idFile, 'user-documents', user_id, 'id-document'); }
          catch (e) { console.error('ID upload failed:', e); }
        }
        if (selfieFile) {
          try { selfieUrl = await uploadToStorage(selfieFile, 'user-selfies', user_id, 'selfie'); }
          catch (e) { console.error('Selfie upload failed:', e); }
        }

        // Using upsert instead of update to ensure record exists even if trigger is slow
        const { error: updateError } = await supabase.from('users').upsert({
          id: user_id,
          email: values.email,
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: values.phone,
          hometown: values.hometown,
          country: values.country,
          status: 'Pending',
          kyc_status: 'Pending',
          ...(idDocumentUrl && { id_document_url: idDocumentUrl }),
          ...(selfieUrl && { selfie_url: selfieUrl }),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' });

        if (updateError) {
          console.error('Failed to save KYC documents to user record:', updateError);
        }

        if (referralCode) {
          try { await ReferralService.createReferral(referralCode, user_id); }
          catch (e) { console.error('Referral error:', e); }
        }

        // Notify User of Welcome
        try {
          await fetch('/api/notifications', {
            method: 'POST',
            body: JSON.stringify({
              user_id,
              type: 'welcome',
              title: 'Welcome to Cardone Capvest!',
              body: 'Your account has been created successfully. Please complete your profile to start investing.',
            }),
          });
        } catch (e) { console.error('Welcome notification failed:', e); }

        // Notify Admins of New Registration and KYC
        if (user_id) {
          try {
            await fetch('/api/notifications', {
              method: 'POST',
              body: JSON.stringify({
                user_id,
                type: 'admin_alert',
                title: 'New User Registration',
                body: `New user ${values.firstName} ${values.lastName} (${values.email}) has registered and is pending approval.${idFile || selfieFile ? ' KYC documents have been uploaded.' : ''}`,
                notifyAdmins: true,
                data: {
                  user_id,
                  has_documents: !!(idFile || selfieFile),
                  email: values.email
                }
              }),
            });
          } catch (e) { console.error('Admin signup notification failed:', e); }
        }
      }

      toast({ title: 'Registration Successful!', description: 'Your account has been created. Please log in.' });

      const redirect = searchParams.get('redirect');
      const loginDest = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';
      router.push(loginDest);
    } catch (err: any) {
      console.error('Signup error:', err);
      toast({
        variant: 'destructive',
        title: 'Registration Failed',
        description: err.message || 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen py-12 px-4 font-body bg-slate-50 dark:bg-slate-950">
      {/* Background with Real Estate texture */}
      <div className="absolute inset-0 z-0">
        <Image
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop"
          alt="Modern Architecture"
          fill
          className="object-cover opacity-30 dark:opacity-40 grayscale transition-opacity duration-1000"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 to-slate-50 dark:from-slate-950/80 dark:to-slate-950" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-xl"
      >
        <ScrollReveal>
          <Card className="bg-card border-border shadow-xl rounded-2xl overflow-hidden">
            <CardHeader className="space-y-4 pb-8 pt-10 text-center bg-muted/30 border-b border-border">
              <div className="flex justify-center items-center gap-12 sm:gap-16 mb-6">
                {STEPS.map((s) => {
                  const ActiveIcon = s.Icon;
                  const isActive = step >= s.id;
                  const isCurrent = step === s.id;

                  return (
                    <div key={s.id} className="relative flex flex-col items-center gap-2">
                      <div className={cn(
                        "w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 z-10",
                        isActive ? "bg-primary text-primary-foreground shadow-lg scale-110" : "bg-muted text-muted-foreground",
                        isCurrent && "ring-2 ring-primary ring-offset-4 ring-offset-background"
                      )}>
                        <ActiveIcon className="w-6 h-6" />
                      </div>

                      <span className={cn(
                        "text-[10px] font-bold uppercase tracking-widest transition-colors duration-500",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}>
                        {s.label}
                      </span>

                      {s.id < STEPS.length && (
                        <div className={cn(
                          "absolute top-6 -right-[4.2rem] sm:-right-[5.2rem] w-[3.5rem] sm:w-[4.5rem] h-[2px] z-0",
                          step > s.id ? "bg-primary" : "bg-muted"
                        )} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="space-y-1">
                <CardTitle className="text-3xl font-bold tracking-tight">Create Account</CardTitle>
                <CardDescription className="text-muted-foreground font-medium">Join Cardone Capvest today</CardDescription>
              </div>
            </CardHeader>

            <CardContent className="pt-8 px-6 sm:px-10 pb-10">
              <Form {...form}>
                <div
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                      e.preventDefault();
                      if (step < STEPS.length) {
                        goNext();
                      }
                    }
                  }}
                  className="space-y-6"
                >
                  <AnimatePresence mode="wait">
                    {step === 1 && (
                      <motion.div
                        key="step1"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-4"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField control={form.control} name="firstName" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold">First Name</FormLabel>
                              <FormControl>
                                <Input placeholder="John" {...field} className="h-11 rounded-lg border-border bg-background transition-all" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="lastName" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold">Last Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Doe" {...field} className="h-11 rounded-lg border-border bg-background transition-all" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                        <FormField control={form.control} name="hometown" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold">Hometown</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. Lagos" {...field} className="h-11 rounded-lg border-border bg-background transition-all" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="country" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold">Country of Origin</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. Nigeria" {...field} className="h-11 rounded-lg border-border bg-background transition-all" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div
                        key="step2"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-4"
                      >
                        <FormField control={form.control} name="email" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold">Email Address</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="john@example.com" {...field} className="h-11 rounded-lg border-border bg-background transition-all" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="phone" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold">Phone Number</FormLabel>
                            <FormControl>
                              <Input type="tel" placeholder="+234..." {...field} className="h-11 rounded-lg border-border bg-background transition-all" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField control={form.control} name="password" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold">Password</FormLabel>
                              <FormControl>
                                <Input type="password" placeholder="••••••••" {...field} className="h-11 rounded-lg border-border bg-background transition-all" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name="confirmPassword" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold">Confirm Password</FormLabel>
                              <FormControl>
                                <Input type="password" placeholder="••••••••" {...field} className="h-11 rounded-lg border-border bg-background transition-all" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div
                        key="step3"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="space-y-6"
                      >
                        <div className="text-center space-y-2">
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            Please provide identity documents for KYC verification.
                            This helps us keep your account secure.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {/* ID */}
                          <div className="space-y-3">
                            <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground text-center">Government ID</label>
                            <label
                              htmlFor="id-upload"
                              className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-xl cursor-pointer hover:bg-muted/50 transition-all group relative overflow-hidden"
                            >
                              {idPreview ? (
                                <Image src={idPreview} alt="ID preview" fill className="object-cover" />
                              ) : (
                                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                  <div className="p-2 bg-muted rounded-full"><Upload className="w-6 h-6" /></div>
                                  <span className="text-[10px] font-bold uppercase">Upload ID</span>
                                </div>
                              )}
                              <input id="id-upload" type="file" accept="image/*,application/pdf" className="sr-only" onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setIdFile(file);
                                  if (file.type.startsWith('image/')) {
                                    const reader = new FileReader();
                                    reader.onload = (ev) => setIdPreview(ev.target?.result as string);
                                    reader.readAsDataURL(file);
                                  }
                                }
                              }} />
                            </label>
                          </div>

                          {/* Selfie */}
                          <div className="space-y-3">
                            <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground text-center">Live Selfie</label>
                            {!cameraActive ? (
                              <div className="space-y-3">
                                <div
                                  className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-xl cursor-pointer hover:bg-muted/50 transition-all group relative overflow-hidden"
                                  onClick={startCamera}
                                >
                                  {selfiePreview ? (
                                    <Image src={selfiePreview} alt="Selfie preview" fill className="object-cover" />
                                  ) : (
                                    <div className="flex flex-col items-center gap-2 text-muted-foreground text-center">
                                      <div className="p-2 bg-muted rounded-full group-hover:bg-primary/10 transition-colors"><Camera className="w-6 h-6" /></div>
                                      <span className="text-[10px] font-bold uppercase">Take Photo</span>
                                      <p className="text-[10px] text-muted-foreground px-4 italic">Click to start camera</p>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 px-2">
                                  <div className="h-[1px] flex-1 bg-border" />
                                  <span className="text-[10px] font-medium text-muted-foreground">OR</span>
                                  <div className="h-[1px] flex-1 bg-border" />
                                </div>
                                <label className="flex items-center justify-center gap-2 w-full p-2 border border-border rounded-lg cursor-pointer hover:bg-muted transition-all">
                                  <Upload className="w-4 h-4 text-muted-foreground" />
                                  <span className="text-xs font-semibold">Upload Selfie Instead</span>
                                  <input type="file" accept="image/*" className="sr-only" onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      setSelfieFile(file);
                                      const reader = new FileReader();
                                      reader.onload = (ev) => setSelfiePreview(ev.target?.result as string);
                                      reader.readAsDataURL(file);
                                    }
                                  }} />
                                </label>
                              </div>
                            ) : (
                              <div className="relative w-full h-40 rounded-xl overflow-hidden border border-primary bg-black">
                                <video
                                  ref={videoRef}
                                  autoPlay
                                  playsInline
                                  muted
                                  className="w-full h-full object-cover scale-x-[-1]"
                                />
                                <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 backdrop-blur-md rounded-full border border-white/20">
                                  <span className="text-[10px] text-white font-medium flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                                    Live
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full w-10 h-10 p-0 shadow-lg"
                                  onClick={capturePhoto}
                                >
                                  <Camera className="w-4 h-4" />
                                </Button>
                                <button
                                  type="button"
                                  className="absolute top-2 right-2 text-white/70 hover:text-white p-1 hover:bg-white/10 rounded-full transition-all"
                                  onClick={stopCamera}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex gap-4 pt-4">
                    {step > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={goPrev}
                        className="flex-1 h-12 rounded-xl font-semibold transition-all"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                      </Button>
                    )}
                    {step < STEPS.length ? (
                      <Button
                        type="button"
                        onClick={goNext}
                        className="flex-1 h-12 rounded-xl font-semibold shadow-md transition-all active:scale-95"
                      >
                        Continue <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => form.handleSubmit(onSubmit)()}
                        className="flex-1 h-12 rounded-xl font-semibold shadow-md transition-all active:scale-95 disabled:opacity-50"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                            Processing...
                          </div>
                        ) : 'Complete Registration'}
                      </Button>
                    )}
                  </div>
                </div>
              </Form>

              <div className="mt-8 text-center text-sm">
                <span className="text-muted-foreground">Already have an account?</span>{' '}
                <Link
                  href={searchParams.get('redirect') ? `/login?redirect=${encodeURIComponent(searchParams.get('redirect')!)}` : "/login"}
                  className="text-primary font-bold hover:underline underline-offset-4"
                >
                  Sign in here
                </Link>
              </div>
            </CardContent>
          </Card>
        </ScrollReveal>
      </motion.div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

// ─── Suspense Wrapper ────────────────────────────────────────────────────────

export default function SignupPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-slate-950 p-4">
        <div className="w-full max-w-xl h-[700px] rounded-[2.5rem] bg-white/5 animate-pulse" />
      </div>
    }>
      <SignupForm />
    </Suspense>
  );
}
